import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Circle, CircleMarker, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Box, Typography } from '@mui/material';
import { DemandSupplyResponse } from '../../services/api';
import { SelectedDemandSupplyTypes } from '../../../types';
import { DEMAND_SUPPLY_COLORS } from './DemandSupplyPanel';
import DemandSupplyDetailCard, { DemandSupplySelection } from './DemandSupplyDetailCard';

// Markers sit on light OSM tiles in both app themes, so always use the light steps.
const MARKER_COLORS = DEMAND_SUPPLY_COLORS.light;

// Scale marker size gently with unit count so big estates read as bigger dots.
const markerRadius = (units: number): number => Math.max(6, Math.min(14, 4 + Math.sqrt(units) / 2.5));

// Extra grace, in px, around every canvas dot's hit test. Small estates render
// as ~6px circles, which is far below a comfortable touch target, and these
// dots sit densely packed over HDB towns — the tolerance widens the tap area
// without inflating the drawn marker and re-crowding the map.
const HIT_TOLERANCE_PX = 10;

// Frame for a whole-island scan — the map fits Singapore instead of a ring.
const SG_BOUNDS = L.latLngBounds([1.15, 103.6], [1.48, 104.1]);

// DOM markers (new launch / GLS) get their tap area from the icon box, so pad a
// transparent wrapper around the visible shape up to a comfortable minimum.
const MIN_TOUCH_PX = 30;

/**
 * Wraps `inner` markup in a transparent, centred box of at least MIN_TOUCH_PX,
 * so a small square/diamond still presents a finger-sized target.
 */
const paddedIcon = (visibleSize: number, inner: string, className: string) => {
  const box = Math.max(MIN_TOUCH_PX, visibleSize);
  return L.divIcon({
    className,
    html: `<div style="width:${box}px;height:${box}px;display:flex;align-items:center;justify-content:center;">${inner}</div>`,
    iconSize: [box, box],
    iconAnchor: [box / 2, box / 2],
  });
};

const projectIcon = L.divIcon({
  className: 'custom-marker',
  html: `<div style="background-color: var(--color-info); width: 32px; height: 32px; border-radius: 4px; border: 2px solid var(--color-white); box-shadow: 0 2px 4px rgba(var(--color-shadow-rgb), 0.3); display: flex; align-items: center; justify-content: center;">
    <svg width="20" height="20" viewBox="0 0 24 24" fill="var(--color-white)" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 7V3H2v18h20V7H12zM6 19H4v-2h2v2zm0-4H4v-2h2v2zm0-4H4V9h2v2zm0-4H4V5h2v2zm4 12H8v-2h2v2zm0-4H8v-2h2v2zm0-4H8V9h2v2zm0-4H8V5h2v2zm10 12h-8v-2h2v-2h-2v-2h2v-2h-2V9h8v10zm-2-8h-2v2h2v-2zm0 4h-2v2h2v-2z"/>
    </svg>
  </div>`,
  iconSize: [32, 32],
  iconAnchor: [16, 16],
});

const newLaunchIcon = (size: number) =>
  paddedIcon(
    size,
    `<div style="background-color: ${MARKER_COLORS.newLaunch}; width: ${size}px; height: ${size}px; border-radius: 4px; border: 2px solid #fff; box-shadow: 0 1px 3px rgba(0,0,0,0.4);"></div>`,
    'custom-marker',
  );

const glsIcon = (size: number) =>
  paddedIcon(
    size,
    `<div style="background-color: ${MARKER_COLORS.gls}; width: ${size}px; height: ${size}px; border: 2px solid #fff; box-shadow: 0 1px 3px rgba(0,0,0,0.4); transform: rotate(45deg);"></div>`,
    'custom-marker',
  );

export interface DemandSupplyMapMarkersProps {
  latitude: number;
  longitude: number;
  data: DemandSupplyResponse | null;
  radius: number; // meters — outer edge of the scan ring
  /** Inner edge of the scan ring in meters; 0 scans from the centre outwards. */
  minRadius?: number;
  selectedTypes: SelectedDemandSupplyTypes;
  showFocusMarker?: boolean;
  showRadiusCircle?: boolean;
  onSelect: (selection: DemandSupplySelection | null) => void;
}

/** Leaflet-only Super Ratio overlays — must render inside a MapContainer. */
const selectOnClick =
  (onSelect: DemandSupplyMapMarkersProps['onSelect'], selection: DemandSupplySelection) =>
  (e: L.LeafletMouseEvent) => {
    // Prevent the map's bare-click handler from clearing selection right after we set it.
    L.DomEvent.stopPropagation(e);
    onSelect(selection);
  };

export const DemandSupplyMapMarkers: React.FC<DemandSupplyMapMarkersProps> = ({
  latitude,
  longitude,
  data,
  radius,
  minRadius = 0,
  selectedTypes,
  showFocusMarker = true,
  showRadiusCircle = true,
  onSelect,
}) => {
  // Own canvas renderer purely so the dots can carry a click tolerance; a
  // renderer instance belongs to one map, so keep it per component instance.
  const hitRenderer = React.useMemo(() => L.canvas({ tolerance: HIT_TOLERANCE_PX }), []);
  const dotOptions = { renderer: hitRenderer, bubblingMouseEvents: false } as const;

  return (
  <>
    {showFocusMarker && (
      <Marker position={[latitude, longitude]} icon={projectIcon}>
        <Popup>
          <strong>{data?.project.name || 'Project Location'}</strong>
        </Popup>
      </Marker>
    )}

    {showRadiusCircle && (
      <Circle
        center={[latitude, longitude]}
        radius={radius}
        interactive={false}
        pathOptions={{
          color: '#3b82f6',
          fillColor: '#3b82f6',
          // A ring shades only the band, so the excluded core stays unfilled.
          fillOpacity: minRadius > 0 ? 0 : 0.05,
          weight: 2,
        }}
      />
    )}

    {/* Inner edge of the scan ring — dashed, to read as a cut-out rather than a target. */}
    {showRadiusCircle && minRadius > 0 && (
      <Circle
        center={[latitude, longitude]}
        radius={minRadius}
        interactive={false}
        pathOptions={{
          color: '#3b82f6',
          fillColor: '#fff',
          fillOpacity: 0.28,
          weight: 1.5,
          dashArray: '5 5',
        }}
      />
    )}

    {selectedTypes.hdbBtoMop &&
      data?.demand.hdb.map((block) => (
        <CircleMarker
          key={`hdb-${block.id}`}
          center={[block.latitude, block.longitude]}
          radius={markerRadius(block.units)}
          pathOptions={{
            ...dotOptions,
            color: '#fff',
            weight: 1,
            fillColor: MARKER_COLORS.hdbBto,
            fillOpacity: 0.85,
          }}
          eventHandlers={{ click: selectOnClick(onSelect, { kind: 'hdb', block }) }}
        />
      ))}

    {selectedTypes.hdbResaleMop &&
      data?.demand.hdbResale?.map((block) => (
        <CircleMarker
          key={`hdb-resale-${block.id}`}
          center={[block.latitude, block.longitude]}
          radius={markerRadius(block.units)}
          pathOptions={{
            ...dotOptions,
            color: '#fff',
            weight: 1,
            fillColor: MARKER_COLORS.hdbResale,
            fillOpacity: 0.55,
          }}
          eventHandlers={{ click: selectOnClick(onSelect, { kind: 'hdbResale', block }) }}
        />
      ))}

    {selectedTypes.condoSsd &&
      data?.demand.condo.map((addr) => (
        <CircleMarker
          key={`condo-${addr.addressId}`}
          center={[addr.latitude, addr.longitude]}
          radius={markerRadius(addr.units)}
          pathOptions={{
            ...dotOptions,
            color: '#fff',
            weight: 1,
            fillColor: MARKER_COLORS.condo,
            fillOpacity: 0.85,
          }}
          eventHandlers={{ click: selectOnClick(onSelect, { kind: 'condo', marker: addr }) }}
        />
      ))}

    {selectedTypes.newLaunch &&
      data?.supply.newLaunch.map((proj) => (
        <Marker
          key={`nl-${proj.id}`}
          position={[proj.latitude, proj.longitude]}
          icon={newLaunchIcon(Math.round(markerRadius(proj.availableUnits) * 2))}
          eventHandlers={{ click: selectOnClick(onSelect, { kind: 'newLaunch', marker: proj }) }}
        />
      ))}

    {selectedTypes.gls &&
      data?.supply.gls.map((site) => (
        <Marker
          key={`gls-${site.id}`}
          position={[site.latitude, site.longitude]}
          icon={glsIcon(Math.round(markerRadius(site.units || 100) * 2))}
          eventHandlers={{ click: selectOnClick(onSelect, { kind: 'gls', site }) }}
        />
      ))}
  </>
  );
};

export interface DemandSupplyMapHudProps {
  data: DemandSupplyResponse | null;
  selectedTypes: SelectedDemandSupplyTypes;
  selection: DemandSupplySelection | null;
  onCloseSelection: () => void;
  legendVariant?: 'light' | 'glass';
  /** When false, only the marker detail card is shown (series chips act as legend). */
  showLegend?: boolean;
}

/** Legend + detail card — render as a sibling of MapContainer inside a relative host. */
export const DemandSupplyMapHud: React.FC<DemandSupplyMapHudProps> = ({
  data,
  selectedTypes,
  selection,
  onCloseSelection,
  legendVariant = 'light',
  showLegend = true,
}) => {
  const legendRow = (color: string, shape: 'circle' | 'square' | 'diamond', label: string) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
      <Box
        sx={{
          width: 10,
          height: 10,
          bgcolor: color,
          borderRadius: shape === 'circle' ? '50%' : shape === 'square' ? '2px' : 0,
          transform: shape === 'diamond' ? 'rotate(45deg)' : 'none',
          border: legendVariant === 'glass' ? '1px solid rgba(255,255,255,0.45)' : '1px solid rgba(0,0,0,0.25)',
          flexShrink: 0,
        }}
      />
      <Typography
        variant="caption"
        sx={{
          fontSize: 11,
          color: legendVariant === 'glass' ? 'rgba(238,246,250,0.9)' : '#333',
        }}
      >
        {label}
      </Typography>
    </Box>
  );

  return (
    <>
      {showLegend && (
        <Box
          sx={
            legendVariant === 'glass'
              ? {
                  position: 'absolute',
                  bottom: 22,
                  right: 12,
                  left: 'auto',
                  zIndex: 1000,
                  bgcolor: 'rgba(8, 18, 28, 0.88)',
                  border: '1px solid rgba(34, 211, 238, 0.22)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: '10px',
                  px: 1.1,
                  py: 0.85,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.25,
                  boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
                  pointerEvents: 'none',
                }
              : {
                  position: 'absolute',
                  bottom: 24,
                  left: 8,
                  zIndex: 1000,
                  bgcolor: 'rgba(255,255,255,0.92)',
                  borderRadius: 1,
                  px: 1,
                  py: 0.75,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.25,
                  boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
                  pointerEvents: 'none',
                }
          }
        >
          {selectedTypes.hdbBtoMop && legendRow(MARKER_COLORS.hdbBto, 'circle', 'BTO HDB MOP')}
          {selectedTypes.hdbResaleMop && legendRow(MARKER_COLORS.hdbResale, 'circle', 'Resale HDB MOP')}
          {selectedTypes.condoSsd && legendRow(MARKER_COLORS.condo, 'circle', 'Condo/EC SSD')}
          {selectedTypes.newLaunch && legendRow(MARKER_COLORS.newLaunch, 'square', 'New Launch')}
          {selectedTypes.gls && legendRow(MARKER_COLORS.gls, 'diamond', 'GLS')}
        </Box>
      )}

      {selection && data && (
        <DemandSupplyDetailCard selection={selection} data={data} onClose={onCloseSelection} />
      )}
    </>
  );
};

interface DemandSupplyMapProps {
  latitude: number;
  longitude: number;
  data: DemandSupplyResponse | null;
  radius: number; // meters — outer edge of the scan ring
  /** Inner edge of the scan ring in meters; 0 scans from the centre outwards. */
  minRadius?: number;
  /** Whole-island scan — no ring to draw, so the map frames Singapore instead. */
  nationwide?: boolean;
  selectedTypes: SelectedDemandSupplyTypes;
}

const DemandSupplyMap: React.FC<DemandSupplyMapProps> = ({
  latitude,
  longitude,
  data,
  radius,
  minRadius = 0,
  nationwide = false,
  selectedTypes,
}) => {
  const mapRef = React.useRef<L.Map | null>(null);
  const [selection, setSelection] = useState<DemandSupplySelection | null>(null);

  useEffect(() => {
    if (!mapRef.current) return;
    if (nationwide) {
      mapRef.current.fitBounds(SG_BOUNDS, { padding: [20, 20] });
      return;
    }
    const center = L.latLng(latitude, longitude);
    mapRef.current.fitBounds(center.toBounds(radius * 2), { padding: [20, 20] });
  }, [latitude, longitude, radius, nationwide]);

  useEffect(() => setSelection(null), [data]);

  const MapController = () => {
    const map = useMap();
    useEffect(() => {
      mapRef.current = map;
    }, [map]);
    return null;
  };

  return (
    <Box sx={{ position: 'relative', width: '100%', height: '100%', minHeight: 400 }}>
      <MapContainer
        center={[latitude, longitude]}
        zoom={13}
        preferCanvas
        style={{ height: '100%', width: '100%', minHeight: 400 }}
      >
        <MapController />
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <DemandSupplyMapMarkers
          latitude={latitude}
          longitude={longitude}
          data={data}
          radius={radius}
          minRadius={minRadius}
          showRadiusCircle={!nationwide}
          selectedTypes={selectedTypes}
          onSelect={setSelection}
        />
      </MapContainer>

      <DemandSupplyMapHud
        data={data}
        selectedTypes={selectedTypes}
        selection={selection}
        onCloseSelection={() => setSelection(null)}
      />
    </Box>
  );
};

export default DemandSupplyMap;
